import { c as _c } from "react/compiler-runtime";
import { LotSelection, useLotSelectionRow } from "@/components/inventory/lot-selection";
import { ShipmentPicker } from "@/components/inventory/shipment-picker";
import { formatQuantity, formatUnit } from "@/lib/format-data";
import { useDoseLotPicker } from "./dose-lot-picker-context";
import { DoseLotReferenceOptions, DoseLotReferenceRows, preventDoseLotEntryDismiss, useDoseLotHistory } from "./dose-lot-reference-options";
import { useDoseLotSelection } from "./use-dose-lot-selection";
function DoseLotQuantityControls() {
  const $ = _c(5);
  const {
    id
  } = useLotSelectionRow();
  const {
    state
  } = useDoseLotPicker();
  let t0;
  if ($[0] !== id || $[1] !== state.selected) {
    t0 = state.selected.find(row => row.id === id)?.quantityShipment;
    $[0] = id;
    $[1] = state.selected;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const shipment = t0;
  if (!shipment) {
    return null;
  }
  let t1;
  if ($[3] !== shipment) {
    t1 = <><ShipmentPicker.QuantityControls shipment={shipment} /><ShipmentPicker.ExtraYieldWarning shipment={shipment} /></>;
    $[3] = shipment;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function DoseStockCoverageSummary() {
  const $ = _c(10);
  const {
    state,
    meta
  } = useDoseLotPicker();
  let t0;
  if ($[0] !== meta || $[1] !== state.stockCoverage) {
    let t1;
    if ($[3] !== meta) {
      t1 = (coverage, index) => {
        const name = meta.inventory.selectedMedication?.stocks?.find(entry => entry.stockId === coverage.stockId)?.stock.name ?? `Stock ${index + 1}`;
        return <p key={coverage.stockId}>{name}: {formatQuantity(coverage.allocatedPhysical)} of{" "}{formatQuantity(coverage.requiredPhysical)}{" "}<span>{coverage.unit ? formatUnit(coverage.unit) : ""}</span><span>{coverage.deltaPhysical > 0 ? " \xB7 above required amount" : coverage.deltaPhysical < 0 ? " \xB7 below required amount" : ""}</span></p>;
      };
      $[3] = meta;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = state.stockCoverage.map(t1);
    $[0] = meta;
    $[1] = state.stockCoverage;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[5] !== state.coverageMessage) {
    t1 = state.coverageMessage && <p role="status" className="text-status-warning-text">{state.coverageMessage}</p>;
    $[5] = state.coverageMessage;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t0 || $[8] !== t1) {
    t2 = <div className="space-y-1 text-caption">{t0}{t1}</div>;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}

/** The same dialog body for every dose: selection capabilities only affect row controls. */
export function DoseLotPickerContent() {
  const $ = _c(57);
  const {
    state,
    actions,
    meta
  } = useDoseLotPicker();
  const value = useDoseLotSelection();
  let t0;
  if ($[0] !== meta.inventory.isCheckingInventory || $[1] !== meta.inventory.locationId || $[2] !== meta.medicationId) {
    t0 = {
      medicationId: meta.medicationId,
      locationId: meta.inventory.locationId,
      isLoadingInventory: meta.inventory.isCheckingInventory
    };
    $[0] = meta.inventory.isCheckingInventory;
    $[1] = meta.inventory.locationId;
    $[2] = meta.medicationId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const historyQuery = useDoseLotHistory(t0);
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <LotSelection.Search />;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== meta.canDiscoverDepleted) {
    t2 = meta.canDiscoverDepleted && <LotSelection.DepletedToggle />;
    $[5] = meta.canDiscoverDepleted;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== actions.toggleReference || $[8] !== historyQuery || $[9] !== meta.medicationName || $[10] !== state.referenceChoices || $[11] !== state.selected.length) {
    t3 = (state.selected.length > 0 || state.referenceChoices.length > 0) && <LotSelection.SelectedSection showHeading={false}><LotSelection.SelectedRows><DoseLotQuantityControls /></LotSelection.SelectedRows><DoseLotReferenceRows choices={state.referenceChoices} onToggle={actions.toggleReference} history={historyQuery.data ?? []} medicationName={meta.medicationName} /></LotSelection.SelectedSection>;
    $[7] = actions.toggleReference;
    $[8] = historyQuery;
    $[9] = meta.medicationName;
    $[10] = state.referenceChoices;
    $[11] = state.selected.length;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== meta.canSelect) {
    t4 = meta.canSelect && <LotSelection.AvailableSection showHeading={false}><LotSelection.AvailableRows /></LotSelection.AvailableSection>;
    $[13] = meta.canSelect;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = meta.medicationId;
  const t6 = meta.medicationName;
  const t7 = meta.inventory.shipments;
  const t8 = state.search;
  let t9;
  if ($[15] !== state.referenceChoices) {
    t9 = state.referenceChoices.map(_temp);
    $[15] = state.referenceChoices;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== actions.addReference || $[18] !== actions.select || $[19] !== actions.setPendingReference || $[20] !== historyQuery || $[21] !== meta.inventory.isCheckingInventory || $[22] !== meta.inventory.shipments || $[23] !== meta.medicationId || $[24] !== meta.medicationName || $[25] !== state.search || $[26] !== t9) {
    t10 = <DoseLotReferenceOptions medicationId={t5} medicationName={t6} historyQuery={historyQuery} shipments={t7} search={t8} references={t9} onAdd={actions.addReference} onSelectShipment={actions.select} onPendingChange={actions.setPendingReference} isLoadingInventory={meta.inventory.isCheckingInventory} />;
    $[17] = actions.addReference;
    $[18] = actions.select;
    $[19] = actions.setPendingReference;
    $[20] = historyQuery;
    $[21] = meta.inventory.isCheckingInventory;
    $[22] = meta.inventory.shipments;
    $[23] = meta.medicationId;
    $[24] = meta.medicationName;
    $[25] = state.search;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== meta.inventory.isCheckingInventory || $[29] !== meta.inventory.previewError) {
    t11 = meta.inventory.isCheckingInventory ? <p role="status" className="text-caption text-foreground-secondary">Checking inventory…</p> : meta.inventory.previewError ? <p role="alert" className="text-caption text-status-warning-text">Inventory requirements could not be verified. Refresh and try again.</p> : null;
    $[28] = meta.inventory.isCheckingInventory;
    $[29] = meta.inventory.previewError;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== state.notice) {
    t12 = state.notice && <p role="status" className="text-caption text-status-warning-text">{state.notice}</p>;
    $[31] = state.notice;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== meta.correctsInventory || $[34] !== meta.drawsInventory || $[35] !== meta.inventory.preview?.ownedLots || $[36] !== state.references || $[37] !== state.selected.length) {
    t13 = meta.drawsInventory && !state.selected.length && state.references.length > 0 && meta.inventory.preview?.ownedLots.some(_temp2) && <p className="text-caption text-status-warning-text">{meta.correctsInventory ? "An inventory correction requires selected inventory lots. Keep the recorded inventory to edit lot numbers only." : "Removing these inventory lots returns their recorded dose amount to inventory."}</p>;
    $[33] = meta.correctsInventory;
    $[34] = meta.drawsInventory;
    $[35] = meta.inventory.preview?.ownedLots;
    $[36] = state.references;
    $[37] = state.selected.length;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== meta.drawsInventory || $[40] !== state.references) {
    t14 = meta.drawsInventory && state.references.length > 0 && <p className="text-caption text-foreground-secondary">Custom lots do not deduct inventory. Only the amounts assigned to inventory lots will be deducted, with no additional deduction at checkout.</p>;
    $[39] = meta.drawsInventory;
    $[40] = state.references;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== meta.drawsInventory) {
    t15 = meta.drawsInventory && <DoseStockCoverageSummary />;
    $[42] = meta.drawsInventory;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== t10 || $[45] !== t11 || $[46] !== t12 || $[47] !== t13 || $[48] !== t14 || $[49] !== t15 || $[50] !== t2 || $[51] !== t3 || $[52] !== t4) {
    t16 = <LotSelection.Frame>{t1}{t2}{t3}{t4}{t10}{t11}{t12}{t13}{t14}{t15}</LotSelection.Frame>;
    $[44] = t10;
    $[45] = t11;
    $[46] = t12;
    $[47] = t13;
    $[48] = t14;
    $[49] = t15;
    $[50] = t2;
    $[51] = t3;
    $[52] = t4;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] !== t16 || $[55] !== value) {
    t17 = <LotSelection.Provider value={value}>{t16}</LotSelection.Provider>;
    $[54] = t16;
    $[55] = value;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  return t17;
}
function _temp2(owned) {
  return owned.outstanding > 0;
}
function _temp(choice) {
  return choice.code;
}
export function DoseLotPickerDialog(t0) {
  const $ = _c(10);
  const {
    title
  } = t0;
  const {
    state,
    actions,
    meta
  } = useDoseLotPicker();
  let t1;
  if ($[0] !== actions) {
    t1 = open => {
      if (!open) {
        actions.close();
      }
    };
    $[0] = actions;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = state.references.length > 0;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DoseLotPickerContent />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== actions.save || $[4] !== meta.allowsEmptySelection || $[5] !== meta.saveDisabled || $[6] !== t1 || $[7] !== t2 || $[8] !== title) {
    t4 = <ShipmentPicker.DialogWrapper open={true} onOpenChange={t1} onSave={actions.save} title={title} allowEmptySelection={meta.allowsEmptySelection} allowIncompleteCoverage={t2} emptySelectionLabel="Use lots" saveLabel="Use lots" saveDisabled={meta.saveDisabled} onEscapeKeyDown={preventDoseLotEntryDismiss}>{t3}</ShipmentPicker.DialogWrapper>;
    $[3] = actions.save;
    $[4] = meta.allowsEmptySelection;
    $[5] = meta.saveDisabled;
    $[6] = t1;
    $[7] = t2;
    $[8] = title;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}